import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Drama } from "@/types/content";

export default function HeroCarousel({ featured }: { featured: Drama[] }) {
  const [index, setIndex] = useState(0);
  const total = featured.length;

  useEffect(() => {
    if (total <= 1) return;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % total);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [total]);

  if (total === 0) return null;

  const goTo = (next: number) => setIndex((next + total) % total);

  return (
    <section className="relative h-[520px] md:h-[640px] w-full overflow-hidden bg-foreground-950">
      {featured.map((drama, i) => (
        <div
          key={drama.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={drama.hero}
            alt={`${drama.title} 主视觉`}
            title={`${drama.title} 剧情介绍及演员信息`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/70 via-foreground-950/40 to-foreground-950/80" />
        </div>
      ))}

      <div className="relative z-10 flex h-full flex-col items-center justify-center px-4 md:px-6 pt-16 text-center">
        <div className="w-full max-w-3xl animate-fade-up" key={featured[index].id}>
          <div className="flex flex-wrap items-center justify-center gap-2 mb-5">
            <span className="rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-background-50">
              {featured[index].type}
            </span>
            <span className="rounded-full border border-white/40 px-3 py-1 text-xs text-white/90">
              {featured[index].year} · {featured[index].region}
            </span>
            <span className="rounded-full border border-white/40 px-3 py-1 text-xs text-white/90">
              评分 {featured[index].rating}
            </span>
          </div>
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-white tracking-tight">
            {featured[index].title}
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-sm md:text-base leading-relaxed text-white/80">
            {featured[index].synopsis}
          </p>
          <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to={`/detail/${featured[index].id}`}
              className="inline-flex w-full sm:w-auto items-center justify-center gap-2 h-11 px-6 rounded-md bg-accent-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-accent-600 transition-colors"
            >
              <i className="ri-play-fill text-lg"></i>
              查看剧情详情
            </Link>
            <Link
              to="/category"
              className="inline-flex w-full sm:w-auto items-center justify-center gap-2 h-11 px-6 rounded-md border border-white/40 text-white text-sm font-medium whitespace-nowrap hover:bg-white/10 transition-colors"
            >
              浏览全部影视
            </Link>
          </div>
        </div>

        <div className="absolute bottom-16 md:bottom-24 left-1/2 -translate-x-1/2 flex items-center gap-2.5">
          {featured.map((drama, i) => (
            <button
              key={drama.id}
              type="button"
              aria-label={`切换到 ${drama.title}`}
              onClick={() => goTo(i)}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${
                i === index ? "w-8 bg-accent-500" : "w-3 bg-white/50 hover:bg-white/80"
              }`}
            />
          ))}
        </div>

        <button
          type="button"
          aria-label="上一张"
          onClick={() => goTo(index - 1)}
          className="absolute left-3 md:left-6 top-1/2 hidden -translate-y-1/2 md:flex items-center justify-center w-10 h-10 rounded-full border border-white/40 text-white hover:bg-white/10 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-xl"></i>
        </button>
        <button
          type="button"
          aria-label="下一张"
          onClick={() => goTo(index + 1)}
          className="absolute right-3 md:right-6 top-1/2 hidden -translate-y-1/2 md:flex items-center justify-center w-10 h-10 rounded-full border border-white/40 text-white hover:bg-white/10 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-xl"></i>
        </button>
      </div>
    </section>
  );
}